/* /admin/ai — AI model providers, guardrails, budget, credits per plan, model per task
 * (prototype ai.js pgAdminAI). */
import { useId, useState } from "react";
import { ApiError } from "../../data/provider";
import { useAdminAi, useAdminTenants, useConnectAiProvider, useDisconnectAiProvider, useUpdateAdminAi } from "../../data/hooks";
import type { AiConfig, AiProvider } from "../../data/types";
import { useI18n } from "../../i18n";
import { fmtN, moneyText } from "../../lib/format";
import { Affix, Alert, Button, Card, Chip, Field, Grid, Icon, Input, Kpi, Loaded, Modal, Money, PageHeader, Toggle, errMsg, useToast } from "../../ui";
import { PLANS } from "../public/components/pricingData";

const KSA_REGIONS = ["Saudi Arabia", "Your KSA data centre"];
export const isKsa = (p: AiProvider) => KSA_REGIONS.includes(p.region) || /Saudi|KSA/.test(p.region);

function ConnectModal({ p, onClose }: { p: AiProvider; onClose: () => void }) {
  const { t, L } = useI18n();
  const toast = useToast();
  const connect = useConnectAiProvider();
  const disconnect = useDisconnectAiProvider();
  const keyId = useId(), modelId = useId();
  const [key, setKey] = useState("");
  const [model, setModel] = useState("");
  const [fe, setFe] = useState<Record<string, string>>({});
  const needsModel = p.models.includes("set-model-id");
  const submit = async () => {
    if (key.trim().length < 8) { setFe({ apiKey: L("API key must be at least 8 characters", "مفتاح API يجب ألا يقل عن 8 أحرف") }); return; }
    try {
      await connect.mutateAsync({ id: p.id, apiKey: key.trim(), model: model.trim() || undefined });
      toast(L(p.name + " connected", "تم ربط " + p.name));
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.fields) setFe(e.fields); else toast(errMsg(e), "bad");
    }
  };
  const doDisconnect = async () => {
    try { await disconnect.mutateAsync(p.id); toast(L(p.name + " disconnected", "تم فصل " + p.name), "info"); onClose(); }
    catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <Modal title={(p.connected ? L("Manage ", "إدارة ") : L("Connect ", "ربط ")) + p.name} onClose={onClose} testId="provider-modal"
      foot={<>
        {p.connected && <Button variant="danger" onClick={doDisconnect} loading={disconnect.isPending} data-testid="btn-disconnect">{L("Disconnect", "فصل")}</Button>}
        <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="pri" icon="lock" onClick={submit} loading={connect.isPending} data-testid="btn-connect-save">{p.connected ? L("Rotate key", "تغيير المفتاح") : t("Connect")}</Button>
      </>}>
      <div className="stack">
        {p.connected && <Alert tone="ok" tint title={L("Connected", "متصل")} desc={<span dir="ltr" className="num">{p.keyMasked}</span>} />}
        <Field label={L("API key", "مفتاح API")} htmlFor={keyId} err={fe.apiKey} hint={L("Encrypted at rest and never shown again — only the first 6 and last 4 characters are kept for display.", "يُشفّر ولا يُعرض مرة أخرى — يُحتفظ بأول 6 وآخر 4 أحرف للعرض فقط.")}>
          <Input id={keyId} type="password" autoComplete="off" dir="ltr" value={key} bad={!!fe.apiKey} onChange={(e) => { setKey(e.target.value); setFe({}); }} data-testid="provider-key" />
        </Field>
        {needsModel && (
          <Field label={L("Model ID", "معرّف النموذج")} htmlFor={modelId} err={fe.model} hint={L("The provider's model name, e.g. the one your contract covers.", "اسم النموذج لدى المزود.")}>
            <Input id={modelId} dir="ltr" value={model} onChange={(e) => setModel(e.target.value)} data-testid="provider-model" />
          </Field>
        )}
        <div className="aiinfo"><Icon name="shield" className="s" /><span>{isKsa(p) ? L("Hosted in Saudi Arabia — allowed for personal data.", "مستضاف في المملكة — مسموح للبيانات الشخصية.") : L("Global API — personal data is not sent here while the PDPL guardrail is on.", "واجهة عالمية — لا تُرسل البيانات الشخصية أثناء تفعيل ضابط حماية البيانات.")}</span></div>
      </div>
    </Modal>
  );
}

function SettingsCards({ cfg }: { cfg: AiConfig }) {
  const { L } = useI18n();
  const toast = useToast();
  const upd = useUpdateAdminAi();
  const budgetId = useId();
  const [budget, setBudget] = useState(String(cfg.budget));
  const [credits, setCredits] = useState<Record<string, string>>(() => Object.fromEntries(PLANS.map((p) => [p, String(cfg.credits[p] ?? 0)])));
  const [fe, setFe] = useState<Record<string, string>>({});
  const save = async (patch: Parameters<typeof upd.mutateAsync>[0], ok: string) => {
    try { await upd.mutateAsync(patch); toast(ok); setFe({}); }
    catch (e) { if (e instanceof ApiError && e.fields) setFe(e.fields); toast(errMsg(e), "bad"); }
  };
  const saveBudget = () => {
    const n = Number(budget);
    if (budget.trim() === "" || !Number.isFinite(n) || n < 0) { setFe({ budget: L("Enter an amount of 0 or more", "أدخل مبلغًا يساوي 0 أو أكثر") }); return; }
    void save({ budget: n }, L("Budget saved", "تم حفظ الميزانية"));
  };
  const saveCredits = () => {
    const e: Record<string, string> = {};
    for (const p of PLANS) { const n = Number(credits[p]); if (credits[p].trim() === "" || !Number.isInteger(n) || n < 0) e["credits." + p] = L("Whole number, 0 or more", "عدد صحيح يساوي 0 أو أكثر"); }
    setFe(e);
    if (Object.keys(e).length) return;
    void save({ credits: Object.fromEntries(PLANS.map((p) => [p, Number(credits[p])])) }, L("Credits per plan saved", "تم حفظ الرصيد لكل باقة"));
  };
  return (
    <div className="stack lg">
      <Card title={L("Guardrails", "الضوابط")} testId="guardrails">
        <div className="stack">
          <div className="list-row"><span className="grow sm">{L("Send personal data only to approved KSA-hosted providers (PDPL)", "إرسال البيانات الشخصية للمزودين المعتمدين داخل المملكة فقط")}</span>
            <Toggle checked={cfg.residency} label={L("Data residency", "موقع البيانات")} testId="toggle-residency" onChange={(v) => save({ residency: v }, v ? L("Personal data restricted to KSA providers", "البيانات الشخصية مقصورة على مزودي المملكة") : L("Residency guardrail turned off", "تم إيقاف ضابط موقع البيانات"))} /></div>
          <div className="list-row"><span className="grow sm">{L("Zero data retention / no training on customer data", "عدم الاحتفاظ بالبيانات أو التدريب عليها")}</span><Chip status="Active">{L("Always on", "مفعل دائمًا")}</Chip></div>
          <div className="list-row"><span className="grow sm">{L("Human review required before saving AI output", "مراجعة بشرية قبل حفظ مخرجات الذكاء")}</span><Chip status="Active">{L("Always on", "مفعل دائمًا")}</Chip></div>
          <Field label={L("Monthly provider budget", "ميزانية المزودين الشهرية")} htmlFor={budgetId} err={fe.budget} hint={L("AI pauses for free-trial customers above 80% of budget", "يتوقف الذكاء لعملاء التجربة عند تجاوز 80% من الميزانية")}>
            <div className="row nw">
              <Affix pre={L("SAR", "ر.س")}><Input id={budgetId} className="num" type="number" min={0} value={budget} bad={!!fe.budget} onChange={(e) => setBudget(e.target.value)} data-testid="ai-budget" /></Affix>
              <Button variant="sm" onClick={saveBudget} disabled={Number(budget) === cfg.budget} data-testid="ai-budget-save">{L("Save", "حفظ")}</Button>
            </div>
          </Field>
        </div>
      </Card>
      <Card title={L("Credits per plan", "الرصيد لكل باقة")} testId="credits-card">
        <div className="stack">
          {PLANS.map((p) => (
            <Field key={p} label={p} htmlFor={"cred-" + p} err={fe["credits." + p]}>
              <Affix post={L("credits / month", "رصيد / شهر")}><Input id={"cred-" + p} className="num" type="number" min={0} value={credits[p]} bad={!!fe["credits." + p]} onChange={(e) => setCredits({ ...credits, [p]: e.target.value })} data-testid={"cred-" + p} /></Affix>
            </Field>
          ))}
          <p className="xs muted" style={{ margin: 0 }}>{L("1 credit ≈ one document or one assistant answer. Customers can buy +1,000 credits for SAR 99/month by bank transfer.", "رصيد واحد ≈ مستند أو إجابة واحدة. يمكن شراء 1,000 رصيد إضافي بـ 99 ر.س شهريًا بالتحويل البنكي.")}</p>
          <div className="row"><Button variant="pri" icon="check" onClick={saveCredits} loading={upd.isPending} data-testid="cred-save">{L("Save credits", "حفظ الرصيد")}</Button></div>
        </div>
      </Card>
    </div>
  );
}

export default function AdminAi() {
  const { t, L } = useI18n();
  const q = useAdminAi();
  const tenants = useAdminTenants();
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <div data-testid="admin-ai">
      <PageHeader title={L("AI models", "نماذج الذكاء الاصطناعي")} sub={L("Connect model providers, choose which model handles each task, and set credits per plan.", "اربط مزودي النماذج واختر النموذج لكل مهمة وحدد الرصيد لكل باقة.")} />
      <Loaded q={q}>{(cfg) => {
        const requests = (tenants.data || []).reduce((a, x) => a + x.aiCredits, 0);
        const spendSar = requests * 0.012 * 3.75;
        const open = cfg.providers.find((p) => p.id === openId);
        const priceOf = (m: string) => { for (const p of cfg.providers) if (m in p.pricePerMTok) return p.pricePerMTok[m]; return null; };
        const providerOf = (m: string) => cfg.providers.find((p) => p.models.includes(m));
        return (
          <>
            <div className="g g-4 mb">
              <Kpi label={L("Provider spend (month, est.)", "تكلفة المزودين (تقديري)")} value={fmtN(spendSar, 0)} unit={L("SAR", "ر.س")} icon="wallet" tone="gold" foot={<>{L("Budget ", "الميزانية ")}<Money value={cfg.budget} dec={0} /></>} />
              <Kpi label={L("Requests (month)", "الطلبات (الشهر)")} value={fmtN(requests, 0)} icon="zap" />
              <Kpi label={L("Connected providers", "المزودون المتصلون")} value={<span data-testid="kpi-connected">{cfg.providers.filter((p) => p.connected).length}</span>} unit={"/ " + cfg.providers.length} icon="link" tone="info" />
              <Kpi label={L("Data residency", "موقع البيانات")} value={cfg.residency ? L("Protected", "محمي") : L("Open", "مفتوح")} icon="shield" tone={cfg.residency ? "" : "warn"} foot={L("personal data → KSA providers only", "البيانات الشخصية ← مزودو المملكة فقط")} />
            </div>
            <Card title={L("Providers & API keys", "المزودون ومفاتيح API")} sub={L("Keys are encrypted and never shown to customers", "المفاتيح مشفرة ولا تظهر للعملاء")} flush testId="providers-card">
              <div className="tw">
                <table className="tbl cards" data-testid="providers-table">
                  <thead><tr><th>{L("Provider", "المزود")}</th><th>{L("Hosting", "الاستضافة")}</th><th>{L("Models", "النماذج")}</th><th className="hide-md">{L("API key", "مفتاح API")}</th><th>{t("Status")}</th><th className="end" aria-label={t("Actions")} /></tr></thead>
                  <tbody>
                    {cfg.providers.map((p) => (
                      <tr key={p.id} data-testid={"provider-" + p.id}>
                        <td className="k1"><b>{p.name}</b></td>
                        <td className="x" data-l={L("Hosting", "الاستضافة")}>{isKsa(p) ? <span className="chip ok"><Icon name="shield" className="s" />{p.region}</span> : p.region}</td>
                        <td className="x num xs" data-l={L("Models", "النماذج")}>{p.models.join(", ")}</td>
                        <td className="hide-md num xs" dir="ltr" data-testid={"key-" + p.id}>{p.keyMasked || "—"}</td>
                        <td className="k2">{p.connected ? <Chip status="Connected" /> : <span className="chip">{t("Not connected")}</span>}</td>
                        <td className="end k3" data-l=""><Button variant={p.connected ? "sm" : "sm pri"} onClick={() => setOpenId(p.id)} data-testid={"btn-provider-" + p.id}>{p.connected ? L("Manage", "إدارة") : t("Connect")}</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            <Grid cols="2-1" className="mt">
              <Card title={L("Model per task", "النموذج لكل مهمة")} sub={L("Platform defaults. Each company owner can switch the model per feature on their AI page.", "الإعدادات الافتراضية للمنصة. يمكن لمالك كل منشأة تغيير النموذج لكل ميزة من صفحة الذكاء.")} flush testId="features-card">
                <div className="tw">
                  <table className="tbl compact">
                    <thead><tr><th>{L("Task", "المهمة")}</th><th>{L("Model", "النموذج")}</th><th className="hide-md">{L("Personal data", "بيانات شخصية")}</th><th className="end">{L("Uses", "الاستخدام")}</th></tr></thead>
                    <tbody>
                      {cfg.features.map((f) => {
                        const prov = providerOf(f.model);
                        const blocked = !prov?.connected || (cfg.residency && f.pii && prov && !isKsa(prov));
                        const price = priceOf(f.model);
                        return (
                          <tr key={f.id} data-testid={"feat-" + f.id}>
                            <td>{L(f.name, f.nameAr)}</td>
                            <td><span className="num xs" dir="ltr">{f.model}</span>{price != null && <span className="xs muted"> · ${price}/M</span>}{blocked && <div className="xs" style={{ color: "var(--warn-ink, inherit)" }}>{!prov?.connected ? L("Provider not connected — mock extractor used", "المزود غير متصل — يُستخدم المستخرج التجريبي") : L("PII stays in KSA — mock extractor used", "البيانات الشخصية داخل المملكة — يُستخدم المستخرج التجريبي")}</div>}</td>
                            <td className="hide-md">{f.pii ? <span className="chip warn">{L("Yes", "نعم")}</span> : <span className="chip">{L("No", "لا")}</span>}</td>
                            <td className="end num">{f.uses}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
              <SettingsCards key={cfg.budget + JSON.stringify(cfg.credits)} cfg={cfg} />
            </Grid>
            <p className="xs muted mt">{L("Spend is estimated from requests at ~", "التكلفة تقديرية من الطلبات بنحو ")}{moneyText(0.045, "en", 3)}{L(" per request.", " لكل طلب.")}</p>
            {open && <ConnectModal p={open} onClose={() => setOpenId(null)} />}
          </>
        );
      }}</Loaded>
    </div>
  );
}
